<template>
  <div class="offset-md-1">
    <div class="form-group row mb-4">
      <label class="col-form-label col-lg-2" v-if="showTitle">
        {{ $t("talents.form.title") }}
      </label>
      <div class="col-lg-10" v-if="talents.length > 0">

        <b-row>
          <b-col lg="10">
            <b-form-group
              :label="$t('search')"
              label-cols="3"
              label-align="right"
              label-size="sm"
              label-for="filter-input"
              class="mb-0"
            >
              <b-input-group size="sm">
                <b-form-input
                  v-model="filter"
                  type="search"
                  id="filter-input"
                  placeholder="..."
                ></b-form-input>
                <b-input-group-append>
                  <b-button :disabled="!filter" @click="filter = ''">
                    {{ $t("clear-search") }}
                    </b-button>
                </b-input-group-append>
              </b-input-group>
            </b-form-group>
          </b-col>
        </b-row>

        <b-table
          ref="selectableTable"
          :selectable="!readonly"
          v-model="displayedTalents"
          :select-mode="selectMode" 
          :items="talents"
          :fields="tableFields"
          :per-page="perPage"
          :current-page="currentPage"
          bordered
          :filter="filter"
          @filtered="onFiltered"
          @row-clicked="selectTalent"
          responsive="sm"
          :class="{
              'talents-table__table': true,
              valid: validateTalents(),
              invalid: validateTalents() === false
            }"
        >
        <template #cell(qualifications)="talent">
          <b-badge variant="info"
          class="p-2 ml-2"
          v-for="qualification in talent.item.qualifications"
          :key="qualification">
            {{ qualification }}
          </b-badge>
        </template>
        <template #cell(name)="talent">
          {{talent.item.lastName}}, {{talent.item.firstName}}
        </template>
        </b-table>
      </div>

      <div class="col-lg-10" v-if="talents.length <= 0 && showCreationLink">
        <router-link tag="a" to="/talents/create" class=" col-lg-10 justify-content-end">
          <label class="talents-table--create-new-talent__label">
            {{ $t("talents.no-talents-found") }}
          </label>
        </router-link>
      </div>
    </div>

    <b-row>
      <b-col class="justify-content-start">
        <p>{{ $t('tasks.table-results', {currentPage, totalPages: Math.round(totalRows/perPage), results: talents.length}) }}</p>
      </b-col>
      <b-col sm="4" class="justfiy-content-end">
        <b-form-group
          :label= "$t('per-page')"
          label-for="per-page-select"
          label-cols-sm="3"
          label-align-sm="right"
          class="mb-0"
        >
          <b-form-select
            id="per-page-select"
            name="per-page-select"
            v-model="perPage"
            :options="pageSizes"
            size="sm"
          ></b-form-select>
        </b-form-group>
      </b-col>
      <b-col sm="5">
        <ul class="pagination pagination-rounded mb-0 justfiy-content-end">
          <b-pagination
            v-model="currentPage"
            :total-rows="totalRows"
            :per-page="perPage">
          </b-pagination>
        </ul>
      </b-col>
    </b-row>

    <div class="table-buttons mt-3">
      <b-button class="m-1" variant="outline-success" @click="selectAllTalents">
        <i class="fa fa-check" aria-hidden="true"></i>
        {{ $t("b-table.select-all") }}
      </b-button>
      <b-button class="m-1" variant="outline-primary" @click="selectFifty">
        <i class="fa fa-user-circle" aria-hidden="true"></i>
        50
      </b-button>
      <b-button class="m-1" variant="outline-primary" @click="selectHundred">
        <i class="fa fa-user-circle" aria-hidden="true"></i>
        100
      </b-button>
      <b-button class="m-1" variant="outline-danger" @click="clearSelected">
        <i class="fa fa-times" aria-hidden="true"></i>
        {{ $t("b-table.clear-all") }}
      </b-button>
    </div>
  </div>
</template>

<script>
import i18n from '@/i18n';
import { required } from "vuelidate/lib/validators";
import { paginationHelper } from '@/helpers/utils/paginationHelper';


export default {
  name: "FrontendPaginatedTalentsTable",
  props: {
    talents: {
      type: Array,
      required: true,
    },
    readonly: {
      type: Boolean,
      default: false,
    },
    showTitle: {
      type: Boolean,
      default: false,
    },
    selectMode: {
      type: String,
      default: "multi",
    },
    preselect: {
      default: "",
    },
    showCreationLink: {
      type: Boolean,
      default: true,
    },
    initialPerPage: {
      // only for initialization
      type: Number,
      default: 10,
    },
    totalPages: {
      type: Number,
      default: 0,
    }
  },
  data() {
    return {
      chosenTalents: [],
      tableFields: [
        { key: 'id', thClass: 'd-none', tdClass: 'd-none' },
        { key: "name", label: i18n.t("talents.overview.form.name") },
        { key: "qualifications", label: i18n.t("talents.form.qualifications") },
        { key: "department", label: i18n.t("talents.form.department") }
      ],
      currentPage: 1,
      modifyPreselected: true,
      displayedTalents: [],
      filter: null,
      totalRows: 1,
      perPage: this.initialPerPage,
      pageSizes: paginationHelper.getPageSizes(),
    };
  },
  watch: {
    perPage: function() {
      this.preselectRows();
    },
    currentPage: function() {
      this.preselectRows();
    },
  },
  validations: {
    chosenTalents: {
      required,
    },
  },
  methods: {
    getRows() {
      return this.totalPages * this.perPage;
    },
    validateTalents() {
      const { $dirty, $error } = this.$v.chosenTalents;
      return $dirty ? !$error : null;
    },
    /**
     * The event MUST be @row-clicked, as pagination/filtering
     * triggers a refresh of the table and fires a @row-selected
     * with NO selected talents afterwards
     * 
     * We therefore manually add the talents to the component's data
     */
    selectTalent(selectedTalents) {
      const index = this.$v.chosenTalents.$model.indexOf(selectedTalents);

      if (index === -1) {
        this.$v.chosenTalents.$model.push(selectedTalents);
      } else {
        this.$v.chosenTalents.$model.splice(index, 1);
      }

      this.$v.$touch();

      if (this.$v.$invalid) {
        return;
      }

      this.emitTalents();
    },
    selectAllTalents() {
      this.clearSelected();
      this.$refs.selectableTable.selectAllRows();
      this.$refs.selectableTable.value.map(row => this.$v.chosenTalents.$model.push(row));
      this.emitTalents();
    },
    clearSelected() {
      this.$v.chosenTalents.$model = [];
      this.$refs.selectableTable.clearSelected();
      this.emitTalents();
    },
    selectFifty() {
      this.$v.chosenTalents.$model = [];
      for (let i = 0; i < 50; i++) {
        if (this.$refs.selectableTable.value[i]) {
          this.$refs.selectableTable.selectRow(i);
          this.$v.chosenTalents.$model.push(this.$refs.selectableTable.value[i]);
        }
      }
      this.emitTalents();
    },
    selectHundred() {
      this.$v.chosenTalents.$model = [];
      for (let i = 0; i < 100; i++) {
        if (this.$refs.selectableTable.value[i]) {
          this.$refs.selectableTable.selectRow(i);
          this.$v.chosenTalents.$model.push(this.$refs.selectableTable.value[i]);
        }
      }
      this.emitTalents();
    },
    emitTalents() {
      this.$emit("talentsTableUpdate", {chosenTalents: this.$v.chosenTalents.$model});
    },
    preselectRows() {
      if (!this.$v.chosenTalents.$model) {
        return;
      }

      // Push preselecting into event queue to be computed as last event
      setTimeout(() => {
        this.$v.chosenTalents.$model.forEach(preselectedTalent => {
          // Only check the currently visible rows for selected talents
          const currentlyDisplayedTalents = this.$refs.selectableTable.value;
          const talentIds = currentlyDisplayedTalents.map(t => t.id);

          // index is zero-based index of the table's rows
          const index = talentIds.indexOf(preselectedTalent.id);
          if (index >= 0) {
            this.$refs.selectableTable.selectRow(index);
          }
        });
      }, 0)
    },
    onFiltered(filteredItems) {
      // Trigger pagination to update the number of buttons/pages due to filtering
      this.totalRows = filteredItems.length
      this.currentPage = 1;
      this.preselectRows();
    },
  },
  mounted() {
    this.totalRows = this.talents.length
  },
  computed: {
    rows() {
      return this.totalPages || 0;
    }
  },
};
</script>

<style lang="scss">
  .talents-table__table.valid {
    border: 1px solid #34c38f !important;
  }

  .talents-table__table.invalid {
    border: 1px solid #f46a6a !important;
  }
  .talents-table--create-new-talent__label {
    cursor: pointer;
  }
  .b-table-row-selected > td {
    background-color: #49505750 !important;
  }
</style>